import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import icons from '../../assets/icons';
import components from '../../components';
import AssetDeviceSelector from '../../components/assetDeviceSelector';
import { setAlert } from '../../redux/dashboard/actions';
import { getSelectedProject } from '../../redux/projects/actions';
import API from '../../services/api';
import utils from '../../utils';

const { CustomButton, CustomInput, IconButton } = components;

const mapAttachedDevices = asset =>
  (asset.attachedDeviceIds || []).map(deviceId => ({ id: deviceId, displayName: deviceId }));

const Asset = ({ asset, site, loadAssets }) => {
  const [active, setActive] = useState(false);
  const [displayName, setDisplayName] = useState(asset.displayName || '');
  const [selectedDevices, setSelectedDevices] = useState(mapAttachedDevices(asset));
  const [submitting, setSubmitting] = useState(false);

  const project = useSelector(getSelectedProject);
  const dispatch = useDispatch();
  const siteId = site?.id || asset.siteId;

  useEffect(() => {
    setDisplayName(asset.displayName || '');
    setSelectedDevices(mapAttachedDevices(asset));
  }, [asset]);

  const updateAsset = async () => {
    if (!project || !displayName.trim()) {
      dispatch(setAlert(utils.generateAlert('Asset Name is required.', 'error')));
      return;
    }

    setSubmitting(true);
    try {
      const requests = [];
      const selectedDeviceIds = selectedDevices.map(device => device.id);
      const devicesToMove = selectedDevices.filter(device => device.requiresDetach).map(device => device.id);
      if (devicesToMove.length > 0) {
        await API.detachAssetDevicesFromOtherSites(project.id, siteId, asset.id, devicesToMove);
      }
      if (displayName.trim() !== (asset.displayName || '')) {
        requests.push(API.updateAsset(project.id, siteId, { ...asset, displayName: displayName.trim() }, ['displayName']));
      }
      if (!utils.compareArrays(selectedDeviceIds, asset.attachedDeviceIds || [])) {
        requests.push(API.updateAssetAttachedDevices(project.id, siteId, asset.id, selectedDeviceIds));
      }
      await Promise.all(requests);
      dispatch(setAlert(utils.generateAlert('Asset updated successfully!', 'success')));
      loadAssets();
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not update this Asset. Try again later.', 'error')));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAsset = async () => {
    if (!project) return;

    setSubmitting(true);
    try {
      await API.deleteAsset(project.id, siteId, asset.id);
      dispatch(setAlert(utils.generateAlert('Asset deleted successfully!', 'success')));
      loadAssets();
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not delete this Asset. Try again later.', 'error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="account-item asset-item">
      <div className={`account-accordion-item ${active ? 'active' : ''}`}>
        <div className="account-title" onClick={() => setActive(!active)}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center asset-summary-field">
              <img className="media-icon" src={icons.media} alt="asset" />
              <h3>{asset.displayName || asset.id}</h3>
            </div>
            <div className="d-flex align-items-center asset-summary-field">
              <img className="user-icon" src={icons.location} alt="site" />
              <h3>{site?.displayName || asset.siteId}</h3>
            </div>
            <div className="d-flex align-items-center asset-summary-field asset-device-count">
              <img className="device-icon" src={icons.deviceOutlineDark} alt="devices" />
              <h3>{`${asset.deviceCount ?? selectedDevices.length} Devices`}</h3>
            </div>
            <img className="arrow-icon" src={icons.angleRight} alt="arrow" />
          </div>
        </div>

        <div className="account-fields asset-fields">
          <div className="form-row">
            <div className="col">
              <div className="form-group">
                <label>Name</label>
                <CustomInput
                  id={`asset-name-${asset.id}`}
                  type="text"
                  value={displayName}
                  isEditable
                  onChange={event => setDisplayName(event.target.value)}
                />
              </div>
            </div>
            <div className="col">
              <div className="form-group">
                <label>Site</label>
                <CustomInput
                  id={`asset-site-${asset.id}`}
                  type="text"
                  value={site?.displayName || asset.siteId}
                  isEditable={false}
                  onChange={() => {}}
                />
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="col">
              <div className="form-group asset-device-section">
                <label>Devices</label>
                <AssetDeviceSelector
                  assetId={asset.id}
                  projectId={project?.id}
                  siteId={siteId}
                  site={site}
                  selectedDevices={selectedDevices}
                  onChange={setSelectedDevices}
                  disabled={submitting || !project}
                  preload={active}
                />
              </div>
            </div>
          </div>
          <div className="form-row justify-content-end">
            <CustomButton
              classes="btn-primary"
              title="Update"
              handleClick={updateAsset}
              loading={submitting}
              disabled={submitting || !project}
            />
          </div>
        </div>
      </div>
      <div className="account-buttons">
        <IconButton
          id={`btn-delete-${asset.id}`}
          btnClasses="account-button"
          tooltip="Remove Asset"
          icon="del"
          disabled={submitting || !project}
          onPress={deleteAsset}
        />
      </div>
    </div>
  );
};

export default Asset;
